import {View} from 'react-native'
import {plural} from '@lingui/core/macro'

import {
  type Classroom,
  classroomMembersLink,
  parentsMayJoin,
  useClassroomActor,
} from '#/state/classrooms'
import {type ClassroomViewer} from '#/state/queries/classrooms'
import {atoms as a, useTheme} from '#/alf'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'

/**
 * A classroom's counts.
 *
 * The profile's `followers / following / posts` line, in the classroom's own
 * terms: the people in the room, split by the seat they hold, and what has been
 * posted to it. Every seat is a way in — tapping `2 teachers` opens the member
 * list on the teachers, the way tapping `26 followers` opens a profile's.
 *
 * The school that runs the classroom is not among the teachers: it holds the
 * admin seat, and a classroom with a school and nobody else has no teachers.
 *
 * The parents count is the one seat a room can decide it does not have: a
 * classroom that keeps parents out says so once, on the page, and a `0 parents`
 * line beside it is the count of a seat nobody there can hold. It is dropped
 * when the room does not accept parents — and kept when it does, or when a
 * parent is somehow already seated in it, because a seat that exists is
 * counted.
 *
 * A count is a *link* only for the people who may open the list behind it. The
 * member list is members-only (`Members.tsx` refuses everybody else), so a
 * visitor is shown the same numbers as plain text: a count that invites a tap
 * and then answers *members only* is a door drawn where there is a wall. The
 * roster tab does not become public for this — the link does.
 *
 * The numbers themselves are the server's counts of the room's seats.
 */
export function ClassroomMetrics({
  classroom,
  viewer,
}: {
  classroom: Classroom
  /** What the server says about the reader. */
  viewer: ClassroomViewer
}) {
  const t = useTheme()
  const actor = useClassroomActor()
  const {teachers, students, parents} = classroom.publishedCounts ?? NO_COUNTS
  const posts = classroom.contentCount ?? 0
  /*
   * The school holds the admin seat in its own room, so one question answers
   * for both the people in the room and the people who run it.
   */
  const mayOpenRoster = !!actor && viewer.role !== null

  return (
    <View
      testID="classroomMetrics"
      style={[a.flex_row, a.flex_wrap, a.gap_sm, a.align_center]}
      pointerEvents="box-none">
      <Metric
        testID="classroomTeachersCount"
        value={teachers}
        label={plural(teachers, {one: 'teacher', other: 'teachers'})}
        to={
          mayOpenRoster ? classroomMembersLink(classroom, 'teacher') : undefined
        }
      />
      <Metric
        testID="classroomStudentsCount"
        value={students}
        label={plural(students, {one: 'student', other: 'students'})}
        to={
          mayOpenRoster ? classroomMembersLink(classroom, 'student') : undefined
        }
      />
      {parentsMayJoin(classroom) || parents > 0 ? (
        <Metric
          testID="classroomParentsCount"
          value={parents}
          label={plural(parents, {one: 'parent', other: 'parents'})}
          to={
            mayOpenRoster
              ? classroomMembersLink(classroom, 'parent')
              : undefined
          }
        />
      ) : null}
      <Text
        testID="classroomPostsCount"
        style={[a.text_md, a.font_semi_bold, t.atoms.text]}>
        {posts}{' '}
        <Text style={[t.atoms.text_contrast_medium, a.font_normal, a.text_md]}>
          {plural(posts, {one: 'post', other: 'posts'})}
        </Text>
      </Text>
    </View>
  )
}

const NO_COUNTS = {teachers: 0, students: 0, parents: 0}

function Metric({
  value,
  label,
  to,
  testID,
}: {
  value: number
  label: string
  /** The member list, opened on this seat — for whoever may open it at all. */
  to?: string
  testID: string
}) {
  const t = useTheme()
  const parts = (
    <>
      <Text style={[a.font_semi_bold, t.atoms.text, a.text_md]}>{value} </Text>
      <Text style={[t.atoms.text_contrast_medium, a.text_md]}>{label}</Text>
    </>
  )
  /*
   * The same count either way, and the difference is exactly one promise: a
   * link where the reader may open the list, plain text where they may not.
   */
  return to ? (
    <InlineLinkText
      testID={testID}
      to={to}
      label={`${value} ${label}`}
      style={[a.flex_row, a.text_md, t.atoms.text]}>
      {parts}
    </InlineLinkText>
  ) : (
    <Text testID={testID} style={[a.flex_row, a.text_md, t.atoms.text]}>
      {parts}
    </Text>
  )
}
